/**
 * Types for `GET /api/v1/sites/{site}/issues` (`backend/api_server
 * /api/issues.py`) — the single-pane issues view backing
 * `src/screens/Support/SupportScreen.tsx`. Unions task/client/worker
 * `Issue` rows with device-source `Alert` rows server-side, so the
 * frontend only ever sees one flat, already-merged shape.
 */

export type IssueSource = 'task' | 'client' | 'worker' | 'device';
export type IssueSeverity = 'critical' | 'warning' | 'info';
export type IssueStatus = 'open' | 'in_progress' | 'resolved';

export interface RawIssue {
  id?: unknown;
  site?: unknown;
  source?: unknown;
  severity?: unknown;
  title?: unknown;
  detail?: unknown;
  firstSeenMs?: unknown;
  lastSeenMs?: unknown;
  count?: unknown;
  reference?: unknown;
  status?: unknown;
  statusUpdatedAtMs?: unknown;
  resolvedAtMs?: unknown;
}

export interface IssueViewModel {
  id: string;
  site: string | null;
  source: IssueSource;
  severity: IssueSeverity;
  title: string;
  detail: Record<string, unknown>;
  firstSeenMs: number;
  lastSeenMs: number;
  count: number;
  /**
   * Triage state, set by platform support (task/client/worker
   * sources) or derived from the device Alert lifecycle. Deliberately
   * customer-visible: an operator should be able to see their problem
   * has been picked up. The support-side detail -- which runbook ran,
   * and who ran it -- is NOT exposed to this tier.
   */
  /**
   * Human-quotable reference (`ALPHA-TASK-20260728-143022`) an
   * operator can read out to support. Derived server-side from
   * immutable fields so both tiers show the same string; `id` remains
   * the canonical lookup key.
   */
  reference: string;
  status: IssueStatus;
  /**
   * When the STATUS last changed -- not when the fault last occurred
   * (that is `lastSeenMs`). This is what tells an operator an
   * in-progress issue is still being actively worked, so conflating
   * the two would claim work is happening when only the fault is
   * recurring.
   */
  statusUpdatedAtMs: number | null;
  resolvedAtMs: number | null;
}

const KNOWN_SOURCES: readonly IssueSource[] = ['task', 'client', 'worker', 'device'];
const KNOWN_SEVERITIES: readonly IssueSeverity[] = ['critical', 'warning', 'info'];
// Unknown/absent status degrades to 'open' rather than throwing -- an
// older api-server that predates triage simply omits the field, and an
// operator seeing everything as open is correct in that case.
const KNOWN_STATUSES: readonly IssueStatus[] = ['open', 'in_progress', 'resolved'];

export function adaptIssue(raw: RawIssue): IssueViewModel {
  const source = KNOWN_SOURCES.includes(raw.source as IssueSource)
    ? (raw.source as IssueSource)
    : 'worker';
  const severity = KNOWN_SEVERITIES.includes(raw.severity as IssueSeverity)
    ? (raw.severity as IssueSeverity)
    : 'info';
  return {
    id: typeof raw.id === 'string' ? raw.id : '',
    site: typeof raw.site === 'string' ? raw.site : null,
    source,
    severity,
    title: typeof raw.title === 'string' ? raw.title : 'Untitled issue',
    detail: raw.detail && typeof raw.detail === 'object' ? (raw.detail as Record<string, unknown>) : {},
    firstSeenMs: typeof raw.firstSeenMs === 'number' ? raw.firstSeenMs : 0,
    lastSeenMs: typeof raw.lastSeenMs === 'number' ? raw.lastSeenMs : 0,
    reference: typeof raw.reference === 'string' ? raw.reference : '',
    status: KNOWN_STATUSES.includes(raw.status as IssueStatus)
      ? (raw.status as IssueStatus)
      : 'open',
    statusUpdatedAtMs:
      typeof raw.statusUpdatedAtMs === 'number' ? raw.statusUpdatedAtMs : null,
    resolvedAtMs: typeof raw.resolvedAtMs === 'number' ? raw.resolvedAtMs : null,
    count: typeof raw.count === 'number' ? raw.count : 1,
  };
}

export function adaptIssueList(raw: unknown): IssueViewModel[] {
  if (!raw || typeof raw !== 'object' || !Array.isArray((raw as { issues?: unknown }).issues)) {
    return [];
  }
  return ((raw as { issues: RawIssue[] }).issues).map(adaptIssue);
}
